import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Play, Star } from "lucide-react";
import { Reveal, Eyebrow, WhatsAppIcon } from "./Reveal";
import { CATALOG_TABS, wa, WA_MSG } from "../constants/site";

export const Catalog = () => {
    const [active, setActive] = useState(CATALOG_TABS[0].id);
    const tab = CATALOG_TABS.find((t) => t.id === active);

    return (
        <section
            id="catalogo"
            className="border-y border-[#222232] bg-[#0E0E14] py-20 md:py-28"
            data-testid="catalog-section"
        >
            <div className="mx-auto max-w-7xl px-5 md:px-8">
                <Reveal className="max-w-2xl">
                    <Eyebrow>Catálogo</Eyebrow>
                    <h2 className="mt-5 text-3xl font-bold text-white sm:text-4xl">
                        Um acervo de cinema, na sua casa.
                    </h2>
                    <p className="mt-4 text-base leading-relaxed text-[#94A3B8] md:text-lg">
                        Marvel, DC, os lançamentos do cinema, as séries mais
                        comentadas e o futebol do fim de semana — navegue e veja
                        o que te espera.
                    </p>
                </Reveal>

                <Reveal delay={0.1}>
                    <div
                        className="mt-10 flex flex-wrap gap-2"
                        data-testid="catalog-tabs"
                    >
                        {CATALOG_TABS.map((t) => (
                            <button
                                key={t.id}
                                data-testid={`catalog-tab-${t.id}`}
                                onClick={() => setActive(t.id)}
                                className={`rounded-full px-5 py-2.5 text-sm font-bold transition-all duration-300 ${
                                    active === t.id
                                        ? "bg-[#E50914] text-white shadow-lg shadow-[#E50914]/30"
                                        : "border border-[#222232] bg-[#14141E] text-[#94A3B8] hover:border-[#E50914]/40 hover:text-white"
                                }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                </Reveal>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={tab.id}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                    >
                        <div className="mt-8 grid grid-cols-3 gap-3 md:grid-cols-6 md:gap-4">
                            {tab.posters.map((poster, i) => (
                                <div
                                    key={`${tab.id}-${poster.title}`}
                                    data-testid={`catalog-poster-${tab.id}-${i}`}
                                    className="group relative aspect-[2/3] cursor-pointer overflow-hidden rounded-xl border border-[#222232] bg-black"
                                >
                                    <img
                                        src={poster.img}
                                        alt={poster.title}
                                        loading="lazy"
                                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent opacity-80 transition-opacity duration-300 group-hover:opacity-100" />
                                    <div className="absolute inset-0 grid place-items-center opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                                        <span className="grid h-12 w-12 place-items-center rounded-full bg-[#E50914] text-white shadow-[0_0_30px_rgba(229,9,20,0.6)]">
                                            <Play className="ml-0.5 h-5 w-5" fill="currentColor" />
                                        </span>
                                    </div>
                                    {poster.rating && (
                                        <span className="absolute right-2 top-2 flex items-center gap-1 rounded-md bg-black/75 px-1.5 py-0.5 text-[11px] font-extrabold text-[#FFB800] backdrop-blur">
                                            <Star className="h-3 w-3" fill="currentColor" />
                                            {poster.rating}
                                        </span>
                                    )}
                                    {poster.live && (
                                        <span className="absolute right-2 top-2 flex items-center gap-1 rounded-md bg-[#E50914] px-1.5 py-0.5 text-[10px] font-extrabold text-white">
                                            <span className="h-1 w-1 rounded-full bg-white animate-pulse-dot" />
                                            AO VIVO
                                        </span>
                                    )}
                                    <div className="absolute bottom-0 left-0 right-0 p-2.5">
                                        <p className="text-[11px] font-bold leading-snug text-white md:text-xs">
                                            {poster.title}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-8 flex flex-col items-start gap-4 rounded-3xl border border-[#E50914]/25 bg-[#14141E] p-6 sm:flex-row sm:items-center sm:justify-between md:p-8">
                            <div>
                                <h3 className="font-display text-lg font-bold text-white md:text-xl">
                                    {tab.title}
                                </h3>
                                <p className="mt-1 text-sm leading-relaxed text-[#94A3B8] md:text-base">
                                    {tab.desc}
                                </p>
                            </div>
                            <a
                                href={wa(WA_MSG.catalog)}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="catalog-whatsapp-btn"
                                className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#25D366] px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-[#25D366]/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#1fb857]"
                            >
                                <WhatsAppIcon className="h-4 w-4" />
                                Ver lista completa
                            </a>
                        </div>
                    </motion.div>
                </AnimatePresence>
            </div>
        </section>
    );
};
